import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ReactComponent as Download } from "../../../assets/icons/download.svg";
import { ReactComponent as LeftArrow } from "../../../assets/icons/leftArrow.svg";
import Dropdown from "../Dropdown";

import { AxiosError } from "axios";
import Skeleton from "react-loading-skeleton";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import { RootState } from "../../../app/store";
import { BarData } from "../../../interface/Trends";
import {
  formatAndSetDataForOverallAnalysis,
  handleDownload,
} from "../../../utils/trendSidebarGraphUtils/overallAnalysisUtils";
import OverallAnalysisGraph from "./OverallAnalysisGraph";
import OverallAnalysisTable from "./OverallAnalysisTable";
import { trendFilters } from "../../../constants/constants";

interface ExpandViewOverallAnalysisProps {
  isSidebarOpen?: boolean;
}

const ExpandViewOverallAnalysis: React.FC<ExpandViewOverallAnalysisProps> = ({
  isSidebarOpen,
}) => {
  const navigate = useNavigate();
  const [trendFilterType, setTrendFilterType] = useState("Most Popular");

  const [loading, setLoading] = useState<boolean>(false);

  const [dynamicColumns, setDynamicColumns] = useState<string[]>([]);
  const [downloadLoading, setDownloadLoading] = useState<boolean>(false);

  const [barData, setBarData] = useState<BarData[] | null>(null);

  const user = useSelector((state: RootState) => state.user.userInfo);

  const fetchBarGraphData = async () => {
    try {
      setLoading(true);
      await formatAndSetDataForOverallAnalysis(
        trendFilterType,
        user,
        setBarData,
        setDynamicColumns
      );
    } catch (error) {
      console.error("Error Fetching bar graph data", error);
      if ((error as AxiosError)?.response?.status !== 400) {
        toast.error("Error Fetching bar graph data");
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchBarGraphData();
  }, [trendFilterType]);

  if (barData && barData.length === 0) {
    return <div className="my-10 text-center">No Bar Data</div>;
  }

  return (
    <div
      className={`bg-white fixed h-[calc(100vh-80px)] ml-2 ${!isSidebarOpen ? "w-[calc(100vw-265px)]" : "w-[calc(100vw-105px)]"} transition-all duration-500 ease-in-out`}
    >
      <div className="flex flex-row justify-between mb-4">
        <div className="flex flex-row mt-6">
          <div>
            <LeftArrow
              className="text-lg ml-4 mt-2 cursor-pointer"
              onClick={() => navigate("/designer-page")}
            />
          </div>
          <div className="transition-all  duration-500 ease-in-out opacity-100 font-semibold max-w-full ml-4  text-secondary ">
            Overall Analysis
          </div>
        </div>
        <div className="mt-2 mr-4 flex">
          {downloadLoading ? (
            <p className="mr-5 mt-4">Loading...</p>
          ) : (
            <button
              onClick={() =>
                handleDownload(setDownloadLoading, barData, dynamicColumns)
              }
            >
              <Download width={25} height={25} className="mr-5 mt-4" />
            </button>
          )}
          <div>
            <div className="text-sm">Filter By</div>
            <div className="w-48">
              <Dropdown
                label={trendFilterType || "Select type"}
                options={trendFilters}
                onSelect={option => {
                  if (option !== trendFilterType) setTrendFilterType(option);
                }}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="overflow-y-auto h-[calc(100vh-150px)]">
        {!barData || loading ? (
          <div className="my-10 flex items-center justify-center">
            <Skeleton height={350} width={1200} />
          </div>
        ) : (
          <div className="my-10 flex items-center justify-center ">
            <OverallAnalysisGraph barData={barData} expanded={true} />
          </div>
        )}

        {!barData || loading ? (
          <div className="my-10 flex items-center justify-center">
            {/* Generate the skeleton rows */}
            {Array.from({ length: 8 }).map((_, index) => (
              <div>
                {Array.from({ length: 7 }).map((_, colIndex) => (
                  <Skeleton
                    key={`${index}-${colIndex}`}
                    width={100}
                    className="mx-4"
                  />
                ))}
              </div>
            ))}
          </div>
        ) : (
          <OverallAnalysisTable
            dynamicColumns={dynamicColumns}
            barData={barData}
          />
        )}
      </div>
    </div>
  );
};

export default ExpandViewOverallAnalysis;
